import type { ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { RefreshCw, Sparkles } from 'lucide-react';
import { Skeleton } from '../ui/Feedback';
import { InlineError } from '../ui/Feedback';
import { Button } from '../ui/Button';
import { spring } from '../ui/motion';

export type AiPhase = 'idle' | 'thinking' | 'result' | 'error';

/** Animated container for idle → thinking (shimmer) → result / error-with-retry. */
export function AiStateView({ phase, idle, result, error, onRetry, thinkingLabel }: { phase: AiPhase; idle: ReactNode; result: ReactNode; error: unknown; onRetry: () => void; thinkingLabel: string }) {
  return (
    <div aria-live="polite" aria-busy={phase === 'thinking'}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div key={phase} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={spring}>
          {phase === 'idle' && idle}
          {phase === 'thinking' && (
            <div className="space-y-3">
              <p className="flex items-center gap-2 text-[0.8125rem] font-medium text-primary-ink">
                <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary-soft">
                  <Sparkles className="h-3.5 w-3.5 animate-pulse" aria-hidden />
                </span>
                {thinkingLabel}
              </p>
              <div className="space-y-2.5">
                <Skeleton className="h-3.5 w-11/12" />
                <Skeleton className="h-3.5 w-full" />
                <Skeleton className="h-3.5 w-3/4" />
                <Skeleton className="h-3.5 w-5/6" />
              </div>
            </div>
          )}
          {phase === 'result' && result}
          {phase === 'error' && (
            <div className="space-y-2.5">
              <InlineError error={error} />
              <Button size="sm" icon={<RefreshCw className="h-4 w-4" />} onClick={onRetry}>
                Retry
              </Button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
